import React, { useState }  from 'react';
import { Box, Card, Typography, Table, TableRow, TableHead, TableBody, TableCell, Button, Select, MenuItem, FormControl, InputLabel, IconButton, Modal, TextField, TableSortLabel, TablePagination, TableContainer } from '@mui/material';import { EmailOutlined, CheckCircleOutline, CancelOutlined,} from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL } from '../../constants.ts';
import { getFloodZoneStatus } from '../../services/floodZoneService.js';
import getGoogleMapsData from '../../services/gmaps.js';

interface Property {
  fullAddress: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  price: number;
  beds: number;
  baths: number;
  year_built: number;
  agent: string;
  agent_email?: string;
  agent_phone?: string;
  details: {
    built: number;
    beds: number;
    baths: number;
    squareft: number;
  };
  images: string[];
  bofa_value: number;
  home_value_80?: number;
  bofa_value_40?: number;
  bofa_value_30?: number;
  motovo_estimate?: number;
  movoto_40?: number;
  movoto_30?: number;
  email_sent?: boolean;
  notes?: string[];
  createdAt?: string;
  last_updated_by?: string;
  last_updated_at?: string;
  property_long?: string;
  property_lat?: string;
}

interface PropertiesListProps {
  properties: Property[];
}

const PropertiesList: React.FC<PropertiesListProps> = ({ properties }) => {
  const [selectedState, setSelectedState] = React.useState<string>('');
  const [hasEmailFilter, setHasEmailFilter] = React.useState<string>('');
  // const [error, setError] = React.useState<string | null>(null);
  const [addressSearch, setAddressSearch] = useState<string>('');
  const [selectedProperty, setSelectedProperty] = useState<any | null>(null);
  const [floodZone, setFloodZone] = useState<{ isFloodZone: boolean; floodZone: string | null; description: string | null } | null>(null);
  const [newNote, setNewNote] = useState('');
  const [createdAtOrder, setCreatedAtOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const formatCreatedAt = (value?: string) => {
    if (!value) return 'N/A';
    const d = new Date(value);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const mergePropertyPreservingComputed = (prev: any, updated: any) => {
    const keep = <T,>(a: T | undefined, b: T | undefined) =>
      (a !== undefined && a !== null ? a : b);
  
    const next = { ...prev, ...updated };
  
    // Only replace images if server sent a non-empty array
    next.images =
      Array.isArray(updated?.images) && updated.images.length > 0
        ? updated.images
        : prev?.images;
  
    // Preserve computed map fields unless server explicitly sets them
    next.google_link = keep(updated?.google_link, prev?.google_link);
    next.property_lat = keep(updated?.property_lat, prev?.property_lat);
    next.property_long = keep(updated?.property_long, prev?.property_long);
  
    return next;
  };

  const handlePropertyClick = async (property: any) => {
    let updatedProperty = { ...property };
    if (!property.images || property.images.length === 0) {
      const mapsData = await getGoogleMapsData(property.fullAddress || property.address);
      updatedProperty = {
        ...updatedProperty,
        images: [mapsData.street_view_image],
        google_link: mapsData.maps_link,
        property_lat: mapsData.latitude,
        property_long: mapsData.longitude
      };
    }
    setSelectedProperty(updatedProperty);
    if (updatedProperty.property_lat && updatedProperty.property_long) {
      const floodInfo = await getFloodZoneStatus(updatedProperty.property_lat, updatedProperty.property_long);
      setFloodZone(floodInfo);
    } else {
      setFloodZone(null);
    }

  };

  const handleCloseModal = () => {
    setSelectedProperty(null);
  };

  const handleStateChange = (event: any) => {
    setSelectedState(event.target.value);
  };

  let filteredProperties = properties;

  if (selectedState) {
    filteredProperties = filteredProperties.filter(property => property.state === selectedState);
  }

  if (hasEmailFilter === 'has') {
    filteredProperties = filteredProperties.filter(property => !!property.agent_email);
   } else if (hasEmailFilter === 'missing') {
    filteredProperties = filteredProperties.filter(property => !property.agent_email);
  }

  const uniqueStates = Array.from(new Set(properties.map(property => property.state))).sort();
  
  if (addressSearch) {
    const searchLower = addressSearch.toLowerCase();
    filteredProperties = filteredProperties.filter(property =>
      (property.fullAddress || property.address || '')
        .toLowerCase()
        .includes(searchLower)
    );
  }

  const sortedProperties = [...filteredProperties].sort((a, b) => {
    const at = a?.createdAt ? new Date(a.createdAt as any).getTime() : 0;
    const bt = b?.createdAt ? new Date(b.createdAt as any).getTime() : 0;
    return createdAtOrder === 'asc' ? at - bt : bt - at;
  });

  async function sendEmail(propertyData) {
    try {
        const response = await fetch('/api/send-email', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(propertyData),
        });

        const result = await response.json();
        if (response.ok) {
            alert('Email sent successfully!');
        } else {
            alert(`Error: ${result.error}`);
        }
    } catch (error) {
        console.error('Error sending email:', error);
        alert('Failed to send email. Please try again.');
    }
}
  return (
    <>
      {/* Filter by State */}
      <Box>
        <Typography variant="h4" sx={{ mb: 2, color: 'grey.100' }}>
          Filters
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <FormControl sx={{ mt: 2, width: '100%', ml: 2  }}>
            <TextField
              id="address-search"
              label="Search Address"
              variant="outlined"
              value={addressSearch}
              onChange={(e) => setAddressSearch(e.target.value)}
              sx={{ mb: 2, width: '100%' }}
            />
          </FormControl>
          <FormControl sx={{ mt: 2, width: '100%', ml: 2 }}>
            <InputLabel id="state-select-label">State</InputLabel>
            <Select
              labelId="state-select-label"
              id="state-select"
              value={selectedState}
              label="State"
              onChange={handleStateChange}
            >
              <MenuItem value="">
                <em>All States</em>
              </MenuItem>
              {uniqueStates.map(state => (
                <MenuItem key={state} value={state}>{state}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ mt: 2, width: '100%', ml: 2 }}>
            {/* Filter by presence of Agent Email */}
            <InputLabel id="has-email-filter-label">Has Email</InputLabel>
            <Select
              labelId="has-email-filter-label"
              id="has-email-filter"
              value={hasEmailFilter}
              label="Has Email"
              onChange={(e) => setHasEmailFilter(e.target.value)}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              <MenuItem value="has">Has Email</MenuItem>
              <MenuItem value="missing">Missing Email</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Results */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ color: 'grey.100' }}>
          Properties List
        </Typography>
      </Box>
      {sortedProperties.length > 0 && (
        <Card sx={{ bgcolor: 'grey.800', p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {selectedState ? `${selectedState} ` : ''}Results ({sortedProperties.length})
            </Typography>
            <Button variant="text" color="inherit" onClick={() => setSelectedState('')}>
              Clear Filter
            </Button>
          </Box>
          <TableContainer sx={{ overflowX: 'auto', overflowY: 'auto', maxWidth: '100%', maxHeight: '65vh' }}>
            <Table stickyHeader sx={{ minWidth: 1400 }}>
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'grey.900' }}>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>Address</TableCell>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>City</TableCell>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>State</TableCell>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>List Price</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper'  }}>80% of List Price</TableCell>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>BOA Home Value</TableCell>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>BOA 40%</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper'  }}>
                    <div>BOA 30%</div>
                    <div>(Max Rehab)</div>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper'  }}>Motovo Home Value</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper'  }}>Motovo 40%</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper'  }}>
                    <div>Motovo 30%</div>
                    <div>(Max Rehab) </div>
                  </TableCell>
                  <TableCell sortDirection={createdAtOrder} sx={{ bgcolor: 'background.paper' }}>
                    <TableSortLabel
                      active
                      direction={createdAtOrder}
                      sx={{ whiteSpace: 'nowrap' }}
                      onClick={() => setCreatedAtOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                    >
                      Created At (ET)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>Agent</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper'  }}>Has Email</TableCell>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedProperties
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((property: Property, index: number) => (
                    <TableRow 
                      key={index + page * rowsPerPage} 
                      onClick={() => handlePropertyClick(property)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'grey.700',
                        },
                      }}>
                      <TableCell>{property.address || 'N/A'}</TableCell>
                      <TableCell>{property.city || 'N/A'}</TableCell>
                      <TableCell>{property.state || 'N/A'}</TableCell>
                      <TableCell>
                        {property.price ? property.price.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A'}
                      </TableCell>

                      <TableCell>
                        {property.home_value_80 != null && !isNaN(Number(property.home_value_80))
                          ? Number(property.home_value_80).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {property.bofa_value != null && !isNaN(Number(property.bofa_value))
                          ? Number(property.bofa_value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {property.bofa_value_40 != null && !isNaN(Number(property.bofa_value_40))
                          ? Number(property.bofa_value_40).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {property.bofa_value_30 != null && !isNaN(Number(property.bofa_value_30))
                          ? Number(property.bofa_value_30).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {property.motovo_estimate != null && !isNaN(Number(property.motovo_estimate))
                          ? Number(property.motovo_estimate).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {property.movoto_40 != null && !isNaN(Number(property.movoto_40))
                          ? Number(property.movoto_40).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {property.movoto_30 != null && !isNaN(Number(property.movoto_30))
                          ? Number(property.movoto_30).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{formatCreatedAt(property.createdAt as any)}</TableCell>
                      <TableCell>{property.agent || 'N/A'}</TableCell>
                      <TableCell>
                        {property.email_sent ? (
                          <CheckCircleOutline color="success" />
                        ) : (
                          <CancelOutlined color="error" />
                        )}
                    </TableCell>
                    <TableCell>
                      {property.agent_email ? (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EmailOutlined />}
                          onClick={() => sendEmail(property)}
                          disabled={property.email_sent}
                        >
                          Send Email
                        </Button>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'warning.main' }}>
                          Agent email was not found
                        </Typography>
                      )}
                    </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={sortedProperties.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Card>
      )}
        {/* Modal for property details */}
        <Modal
          open={!!selectedProperty}
          onClose={handleCloseModal}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box
            sx={{
              width: '80%',
              bgcolor: 'grey.900',
              borderRadius: 2,
              p: 3,
              boxShadow: 24,
              maxHeight: '90vh',
              minHeight: '500px',
              overflowY: 'auto',
              color: 'white',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
              <Box sx={{width: '50%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end'}}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ color: 'grey.100' }}>
                    Address: {
                      selectedProperty?.fullAddress ||
                      selectedProperty?.address ||
                      'N/A'
                    }
                  </Typography>
                </Box>
                {/* Images Section */}
                <Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {selectedProperty?.images?.length > 0 ? (
                      selectedProperty.images.map((image: string, index: number) => (
                        <img
                          key={index}
                          src={image}
                          alt={`Property ${index + 1}`}
                          style={{ width: '100%', height: 'auto', objectFit: 'cover', borderRadius: '8px' }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: 'grey.600' }}>
                        No images available
                      </Typography>
                    )}
                  </Box>
                  {/* Notes, Last Updated By/At Section */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ color: 'grey.100', mb: 1 }}>
                      Notes
                    </Typography>
                    {selectedProperty?.notes && selectedProperty.notes.length > 0 ? (
                      selectedProperty.notes.map((note: string, index: number) => (
                        <Typography key={index} variant="body2" sx={{ color: 'grey.300', mb: 0.5 }}>
                          - {note}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: 'grey.500' }}>
                        No notes available.
                      </Typography>
                    )}
                               {/* New Note Input & Save */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <TextField
                        id="new-note"
                        label="Add note"
                        variant="outlined"
                        size="small"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        sx={{ flexGrow: 1 }}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        sx={{ ml: 1 }}
                        onClick={async () => {
                          if (!newNote.trim()) return;
                          try {
                            const response = await fetch(`${API_BASE_URL}/api/properties/update/${selectedProperty._id}`, {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ notes: [...(selectedProperty.notes || []), newNote.trim()] }),
                            });
                            if (!response.ok) throw new Error(`HTTP ${response.status}`);
                            const updated = await response.json();
                            setSelectedProperty(prev => mergePropertyPreservingComputed(prev, updated));
                            setNewNote('');
                          } catch (err) {
                            console.error('Failed to save note:', err);
                            alert('Could not save note. Please try again.');
                          }
                        }}
                      >
                        Add
                      </Button>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'grey.500', mt: 2, display: 'block' }}>
                      Last Updated By: {selectedProperty?.last_updated_by || 'N/A'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'grey.500' }}>
                      Last Updated At: {selectedProperty?.last_updated_at
                        ? new Date(selectedProperty.last_updated_at).toLocaleString()
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* Property Details Section */}
                <Box sx={{width: '50%', height: '500px'}}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: 'grey.100', fontWeight: 'bold', textAlign: 'center' }}>
                        Property Details
                      </Typography>
                      <IconButton
                        onClick={handleCloseModal}
                        sx={{ position: 'absolute', top: 0, right: 0 }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    {/* Row 1: City, State, ZIP */}
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'space-evenly', alignContent: 'center', width: '100%', mb: 1}}>
                      <Box sx={{width: '25%', ml: 2}}>
                        <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>City:</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.100' }}>{selectedProperty?.city || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}>
                        <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>State:</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.100' }}>{selectedProperty?.state || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}>
                        <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>ZIP Code:</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.100' }}>{selectedProperty?.zip || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}>
                        <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>Beds / Baths:</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.100' }}>
                          {selectedProperty?.details?.beds || 'N/A'} / {selectedProperty?.details?.baths || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Row 2: Price, Beds / Baths, Year Built */}
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'space-evenly', alignContent: 'center', width: '100%', mb: 1 }}>
                      <Box sx={{width: '25%', ml: 2}}>
                        <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>List Price:</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.100' }}>
                          {selectedProperty?.price ? selectedProperty?.price.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}>
                        <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>Tier:</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.100' }}>
                          {selectedProperty?.tier ? selectedProperty?.tier : 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}>
                        <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>Population Density:</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.100' }}>
                          {selectedProperty?.details?.built || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}>
                        <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>Flood Zone:</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.100' }}>
                          {floodZone
                            ? floodZone.isFloodZone
                              ? `${floodZone.floodZone || 'Zone Unknown'} - ${floodZone.description || 'No description'}`
                              : 'Not in flood zone'
                            : 'Checking...'}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Row 4: Property Calculations */}
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'space-evenly', alignContent: 'center', width: '100%', mb: 1, ml: 1, mr: 1 }}>
                      <Box sx={{width: '25%', ml: 2}}>
                        <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>BOA Home Value:</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.100' }}>
                        {
                          selectedProperty?.bofa_value != null && !isNaN(Number(selectedProperty.bofa_value))
                            ? Number(selectedProperty.bofa_value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                            : 'N/A'
                        }
                        </Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}>
                          <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>BOA 40%:</Typography>
                          <Typography variant="body2" sx={{ color: 'grey.100' }}>
                          {
                            selectedProperty?.bofa_value_40 != null && !isNaN(Number(selectedProperty.bofa_value_40))
                              ? Number(selectedProperty.bofa_value_40).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                              : 'N/A'
                          }
                          </Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}> 
                          <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>BOA 30%:</Typography>
                          <Typography variant="body2" sx={{ color: 'grey.100' }}>
                          {
                            selectedProperty?.bofa_value_30 != null && !isNaN(Number(selectedProperty.bofa_value_30))
                              ? Number(selectedProperty.bofa_value_30).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                              : 'N/A'
                          }
                          </Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}> 
                          <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>80% of List Price:</Typography>
                          <Typography variant="body2" sx={{ color: 'grey.100' }}>
                          {
                            selectedProperty?.home_value_80 != null && !isNaN(Number(selectedProperty.home_value_80 ))
                              ? Number(selectedProperty.home_value_80 ).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                              : 'N/A'
                          }
                          </Typography>
                      </Box>
                    </Box>
                    {/* Row 3: Agent */}
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'space-evenly', alignContent: 'center', width: '100%', mb: 1, ml: 1, mr: 1 }}>
                      <Box sx={{width: '25%', ml: 2}}>
                        <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>Agent:</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.100' }}>
                          {selectedProperty?.agent || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}>
                          <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>Agent Phone:</Typography>
                          <Typography variant="body2" sx={{ color: 'grey.100' }}>
                            {selectedProperty?.agent_phone || 'N/A'}
                          </Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}> 
                          <Typography variant="body1" sx={{ color: 'grey.100', fontWeight: 'bold' }}>Agent Email:</Typography>
                          <Typography variant="body2" sx={{ color: 'grey.100' }}>
                            {selectedProperty?.agent_email || 'N/A'}
                          </Typography>
                      </Box>
                      <Box sx={{width: '25%', ml: 2}}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EmailOutlined />}
                          onClick={() => sendEmail(selectedProperty)}
                          sx={{ mt: 2 }}
                          disabled={!selectedProperty?.agent_email || selectedProperty?.email_sent}
                        >
                          Send Email
                        </Button>
                      </Box>
                    </Box>
                    {/* Row 5: Other helpers */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', mb: 2}}>
                      <Box sx={{ width: '25%', ml: 2 }}>
                        {selectedProperty?.google_link ? (
                          <Typography variant="body2" sx={{ color: 'primary.main' }}>
                            <a
                              href={selectedProperty.google_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textDecoration: 'none', color: 'inherit', fontWeight: 'bold' }}
                            >
                              Google Street View
                            </a>
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'grey.100' }}>N/A</Typography>
                        )}
                      </Box>
                    </Box>
                  <Box>
                    
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Modal>
    </>
  );
};

export default PropertiesList;